import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import PandaViewSimplePreview from './panda-view-simple-previews/panda-view-simple-preview';

/**
 * A container for the PandaView simple preview with orientation, wallet mode, and overlays
 *
 * @param {object} props
 * @param {React.ReactNode} [props.children] - Content to be rendered inside the container
 * @param {React.ReactNode} [props.topRightOverlay] - Content for the top right overlay
 * @param {React.ReactNode} [props.bottomRightOverlay] - Content for the bottom right overlay
 * @param {object} [props.raw_dimensions] - The raw dimensions of the image to determine orientation
 * @param {number} props.raw_dimensions.w - The width
 * @param {number} props.raw_dimensions.h - The height
 * @param {boolean} props.isWallet - Whether the preview is for a wallet print
 * @param {React.ReactElement} [props.placeholder] - When provided, renders this element in place of
 *                                                    `PandaViewSimplePreview`. Cloned once per slot
 *                                                    (twice for wallet prints). Use for loading states
 *                                                    such as a BlurhashCanvas before the thumbnail arrives.
 * @param {object} [props.pandaPreviewStyle] - Extra inline styles applied to the inner .panda-preview div
 */
const PandaViewPreviewContainer = ({
  children,
  topRightOverlay,
  bottomRightOverlay,
  placeholder,
  pandaPreviewStyle,
  ...props
}) => {
  const { raw_dimensions: rawDimensions, isWallet } = props;

  return (
    <div
      className={classNames(
        'preview d-flex justify-content-center align-items-center m-auto',
        {
          landscape: rawDimensions && rawDimensions.h <= rawDimensions.w,
          portrait: rawDimensions && rawDimensions.h > rawDimensions.w,
          wallet: isWallet,
        },
      )}>
      <div
        className="panda-preview d-flex position-relative"
        style={pandaPreviewStyle}>
        {topRightOverlay && (
          <div className="panda-preview-overlay top-right">
            {topRightOverlay}
          </div>
        )}
        {(isWallet ? ['left', 'right'] : ['single']).map((key) =>
          placeholder ? (
            React.cloneElement(placeholder, { key })
          ) : (
            <PandaViewSimplePreview key={key} {...props} />
          ),
        )}
        {children}
        {bottomRightOverlay && (
          <div className="panda-preview-overlay bottom-right">
            {bottomRightOverlay}
          </div>
        )}
      </div>
    </div>
  );
};

PandaViewPreviewContainer.propTypes = {
  children: PropTypes.node,
  raw_dimensions: PropTypes.objectOf(PropTypes.number),
  isWallet: PropTypes.bool.isRequired,
  topRightOverlay: PropTypes.node,
  bottomRightOverlay: PropTypes.node,
  placeholder: PropTypes.element,
  pandaPreviewStyle: PropTypes.shape({
    width: PropTypes.string,
    height: PropTypes.string,
    overflow: PropTypes.string,
  }),
};

PandaViewPreviewContainer.defaultProps = {
  children: null,
  raw_dimensions: null,
  topRightOverlay: null,
  bottomRightOverlay: null,
  placeholder: null,
  pandaPreviewStyle: undefined,
};

export { PandaViewPreviewContainer };
